import { ReportMachineTimeline } from "../../../types/mes";
import { t } from "../../../i18n/locale";
import { minutesToClock } from "./reportFormat";

/**
 * Gantt po strojevima za dnevni izvještaj: jedan redak po stroju, gornja traka
 * planirano, donja ostvareno.
 *
 * Vraća SVG kao string, a ne React komponentu, jer isti graf mora završiti i u
 * dokumentu za ispis — a ispis se gradi kao samostalni HTML string u novom
 * prozoru i nema pristup React drvetu.
 */

export const PLAN_COLOR = "#2980b9";
export const ACTUAL_COLOR = "#27ae60";

const LABEL_WIDTH = 156;
const PAD_RIGHT = 14;
const PAD_TOP = 26;
const ROW_HEIGHT = 36;
const AXIS_HEIGHT = 24;
const BAR_HEIGHT = 7;

interface Theme {
  text: string;
  textMuted: string;
  grid: string;
  axis: string;
  rowAlt: string;
}

const THEMES: Record<"dark" | "print", Theme> = {
  dark: {
    text: "#e8e6e3",
    textMuted: "#adb3bd",
    grid: "#2f3542",
    axis: "#4a5160",
    rowAlt: "rgba(255,255,255,0.025)",
  },
  print: {
    text: "#2c3e50",
    textMuted: "#566573",
    grid: "#e5e7e9",
    axis: "#bdc3c7",
    rowAlt: "#fafbfb",
  },
};

const esc = (value: string): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export interface MachineTimelineSvgOptions {
  theme?: "dark" | "print";
  width?: number;
}

export const buildMachineTimelineSvg = (
  timeline: ReportMachineTimeline,
  options: MachineTimelineSvgOptions = {}
): string => {
  const theme = THEMES[options.theme ?? "dark"];
  const width = options.width ?? 1000;
  const lanes = timeline.lanes ?? [];

  if (lanes.length === 0) return "";

  const height = PAD_TOP + lanes.length * ROW_HEIGHT + AXIS_HEIGHT;
  const plotLeft = LABEL_WIDTH;
  const plotRight = width - PAD_RIGHT;
  const plotWidth = plotRight - plotLeft;

  const axisMin = timeline.min_minutes;
  const axisMax = timeline.max_minutes;
  const range = Math.max(1, axisMax - axisMin);

  const xOf = (minutes: number) =>
    plotLeft + ((Math.min(Math.max(minutes, axisMin), axisMax) - axisMin) / range) * plotWidth;

  // Gusti sat po satu postaje nečitljiv iznad ~10 h proizvodnje.
  const tickStep = range <= 600 ? 60 : 120;
  const firstTick = Math.ceil(axisMin / tickStep) * tickStep;
  const ticks: number[] = [];
  for (let t = firstTick; t <= axisMax; t += tickStep) ticks.push(t);

  const parts: string[] = [];

  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="100%" ` +
      `height="${height}" font-family="Roboto, Arial, Helvetica, sans-serif" role="img" ` +
      `aria-label="${t("Timeline strojeva, planirano i ostvareno")}">`
  );

  // Legenda
  const legendY = 12;
  parts.push(
    `<rect x="${plotRight - 168}" y="${legendY - 6}" width="16" height="7" rx="2" fill="${PLAN_COLOR}"/>` +
      `<text x="${plotRight - 148}" y="${legendY + 1}" font-size="10" fill="${theme.textMuted}">${t("Planirano")}</text>` +
      `<rect x="${plotRight - 88}" y="${legendY - 6}" width="16" height="7" rx="2" fill="${ACTUAL_COLOR}"/>` +
      `<text x="${plotRight - 68}" y="${legendY + 1}" font-size="10" fill="${theme.textMuted}">${t("Ostvareno")}</text>`
  );

  // Satnice
  for (const tick of ticks) {
    const x = xOf(tick);
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${PAD_TOP}" x2="${x.toFixed(1)}" ` +
        `y2="${PAD_TOP + lanes.length * ROW_HEIGHT}" stroke="${theme.grid}" stroke-width="1"/>` +
        `<text x="${x.toFixed(1)}" y="${height - 8}" font-size="10" fill="${theme.textMuted}" ` +
        `text-anchor="middle">${minutesToClock(tick)}</text>`
    );
  }

  lanes.forEach((lane, index) => {
    const rowTop = PAD_TOP + index * ROW_HEIGHT;
    const center = rowTop + ROW_HEIGHT / 2;

    if (index % 2 === 1) {
      parts.push(
        `<rect x="${plotLeft}" y="${rowTop}" width="${plotWidth}" height="${ROW_HEIGHT}" fill="${theme.rowAlt}"/>`
      );
    }

    parts.push(
      `<text x="8" y="${center - 2}" font-size="10.5" font-weight="600" fill="${theme.text}">${esc(
        lane.machine
      )}</text>` +
        `<text x="8" y="${center + 10}" font-size="9" fill="${theme.textMuted}">${esc(lane.type)}</text>`
    );

    const drawSegments = (
      segments: typeof lane.plan,
      color: string,
      y: number,
      kind: string
    ) => {
      for (const segment of segments) {
        const x = xOf(segment.start_min);
        const w = Math.max(1.5, xOf(segment.end_min) - x);
        parts.push(
          `<rect x="${x.toFixed(1)}" y="${y}" width="${w.toFixed(1)}" height="${BAR_HEIGHT}" rx="2" ` +
            `fill="${color}"><title>${esc(segment.label)} — ${kind} ${minutesToClock(
              segment.start_min
            )}-${minutesToClock(segment.end_min)}</title></rect>`
        );
      }
    };

    drawSegments(lane.plan, PLAN_COLOR, center - BAR_HEIGHT - 2, "planirano");
    drawSegments(lane.actual, ACTUAL_COLOR, center + 2, "ostvareno");

    if (index < lanes.length - 1) {
      const y = rowTop + ROW_HEIGHT;
      parts.push(
        `<line x1="0" y1="${y}" x2="${plotRight}" y2="${y}" stroke="${theme.grid}" ` +
          `stroke-width="1" stroke-dasharray="3 3"/>`
      );
    }
  });

  const axisY = PAD_TOP + lanes.length * ROW_HEIGHT;
  parts.push(
    `<line x1="${plotLeft}" y1="${axisY}" x2="${plotRight}" y2="${axisY}" stroke="${theme.axis}" stroke-width="1"/>`
  );

  parts.push(`</svg>`);
  return parts.join("");
};
